<script setup>
import {computed, onBeforeMount, ref} from "vue";
import {useStore} from "vuex";
import Swal from "sweetalert2";
import TwoIntegrations from '@/components/Dashboard/TwoIntegrations';
import FlowGroup from '@/components/Dashboard/FlowGroup';
import FormButton from '@/components/Shared/Form/Button';
import FlowDataMappingGroup from '@/Pages/Dashboard/Integrations/Flows/Partials/FlowDataMappingGroup';
import FlowDataMappingConfigureModal from '@/Pages/Dashboard/Integrations/Flows/Partials/FlowDataMappingConfigureModal';
import ConfirmationModal from '@/components/Dashboard/ConfirmationModal';
import ManualLinkButton from '@/components/Shared/ManualLinkButton.vue';
import FlowLoadTemplateModal from "@/Pages/Dashboard/Integrations/Flows/Partials/FlowLoadTemplateModal.vue";
import axios from "axios";
import FlowStoreTemplateModal from "@/Pages/Dashboard/Integrations/Flows/Partials/FlowStoreTemplateModal.vue";

const store = useStore()

const props = defineProps({
  user: Object,
  manualLink: String,
})

const formData = ref([])
const flow = ref(null)
const firstFlowFields = ref([])
const secondFlowFields = ref([])
const mappingFields = ref([])
const microfunctions = ref([])
const initialLoading = ref(true)
const processingBackToDefault = ref(false)
const currentMappingFieldSide = ref(null)
const currentMappingFieldApplication = ref(null)
const currentMappingFieldFlowId = ref(null)
const currentMappingField = ref(null)
const currentMappingFieldOptions = ref(null)
const showConfigureMappingFieldModal = ref(false)
const showConfirmationModal = ref(false)
const showLoadTemplateModal = ref(false)
const showStoreTemplateModal = ref(false)

const isAdmin = computed(() => store.getters["flowsDetails/isAdmin"])
const flowGroup = computed(() => store.getters["flowsDetails/flowGroup"])
const userIntegration = computed(() => store.getters["flowsDetails/userIntegration"])
const userIntegrationFlow = computed(() => store.getters["flowsDetails/userIntegrationFlow"])

const application1 = computed(() => userIntegration.value.first_application)
const application2 = computed(() => userIntegration.value.second_application)
const integrationFlowId = computed(() => route().params.integrationFlow)
const integrationId = computed(() => route().params.integration)

const handleError = (error) => {
  Swal.fire({
    text: error.message,
    icon: 'error',
    showConfirmButton: false,
    timer: 2000,
  });
}

const configureMappingField = (isFirstSide, mappingField) => {
  const application = isFirstSide ? application1.value.application.name : application2.value.application.name
  const flowId = isFirstSide ? userIntegrationFlow.value.first_flow_id : userIntegrationFlow.value.second_flow_id
  const options = isFirstSide ? firstFlowFields.value : secondFlowFields.value

  currentMappingFieldSide.value = isFirstSide
  currentMappingFieldApplication.value = application
  currentMappingFieldFlowId.value = flowId
  currentMappingField.value = mappingField
  currentMappingFieldOptions.value = options

  showConfigureMappingFieldModal.value = true
}

const closeModal = () => showConfigureMappingFieldModal.value = false
const resetModal = () => currentMappingField.value = null
const backToDefault = () => {
  processingBackToDefault.value = true

  axios
    .delete(`/api/integrations/${integrationId.value}/flows/${integrationFlowId.value}/data-mapping`)
    .then(() => {
      Swal.fire({
        text: trans('dashboard.alerts.success_revert_data_mapping'),
        icon: 'success',
        showConfirmButton: false,
        timer: 2000,
      });

      fetchDataMapping()
    })
    .catch(error => handleError(error.response.data))
    .finally(() => processingBackToDefault.value = false)
}

onBeforeMount(() => {
  axios
    .get(`/api/integrations/${integrationId.value}/flows/${integrationFlowId.value}/data-mapping`)
    .then(response => {
      const data = response.data.data

      firstFlowFields.value = data.firstFlowFields
      secondFlowFields.value = data.secondFlowFields
      mappingFields.value = data.mappingFields
      microfunctions.value = data.microfunctions
    })
    .catch(error => handleError(error.response.data))
    .finally(() => initialLoading.value = false)
})
</script>
<template>
  <div>
    <div class="tile my-4 px-3 py-4">
      <flow-group :flowGroup="flowGroup"/>
    </div>
    <two-integrations
      :integration1="{ ...application1.application, alias: application1.alias }"
      :integration2="{ ...application2.application, alias: application2.alias }"
      class="my-4"
    />
    <div class="tile my-4 p-0">
      <loading-view :loading="initialLoading">
        <div class="grid grid-3 px-6 py-3 align-items-center text-center" style="border-bottom: 2px solid #ccc">
          <p class="text-lg">
            {{ trans('dashboard.integrations.flows.data_mapping.field_in_x', {application: application1.application.name}) }}
          </p>
          <p class="text-lg">
            {{ trans('dashboard.integrations.flows.data_mapping.reference_field') }}
          </p>
          <p class="text-lg">
            {{ trans('dashboard.integrations.flows.data_mapping.field_in_x', {application: application2.application.name}) }}
          </p>
        </div>

        <!-- Flow mapping form -->
        <flow-data-mapping-group
          :grouped-mapping-fields="mappingFields"
          :initial="true"
          @configure="configureMappingField"
        />

        <!-- Flow mapping buttons -->
        <div v-if="$can('flows.data-mapping.update')" class="flex items-center my-6 mr-3 ml-1">
          <div class="ml-auto">
            <manual-link-button :manual-link="manualLink"/>

            <button
              v-if="isAdmin"
              class="button button_success"
              type="button"
              v-text="trans('Store as template')"
              @click="showStoreTemplateModal = true"
            />

            <button
              v-if="isAdmin"
              class="button"
              type="button"
              v-text="trans('Load from template')"
              @click="showLoadTemplateModal = true"
            />

            <form-button
              :processing="processingBackToDefault"
              :value="trans('dashboard.integrations.flows.back_to_default')"
              class="mr-6"
              @click.native="showConfirmationModal = true"
            />
          </div>
        </div>

        <!-- Configure data mapping field modal -->
        <flow-data-mapping-configure-modal
          :application="currentMappingFieldApplication"
          :flow-id="currentMappingFieldFlowId"
          :is-first-side="currentMappingFieldSide"
          :mapping-field="currentMappingField"
          :micro-functions="microfunctions"
          :options="currentMappingFieldOptions"
          :show="showConfigureMappingFieldModal"
          :user-integration-flow-id="integrationFlowId"
          :user-integration-id="integrationId"
          @close="closeModal"
          @closed="resetModal"
        />

        <confirmation-modal
          :confirm-button-text="trans('common.confirm')"
          :content="trans('dashboard.integrations.flows.data_mapping.back_to_default_confirmation_description')"
          :header="trans('dashboard.integrations.flows.confirm_action')"
          :show-modal="showConfirmationModal"
          @close="showConfirmationModal = false"
          @confirm="backToDefault"
        />

        <flow-load-template-modal
          v-if="isAdmin"
          :show="showLoadTemplateModal"
          :user-integration-id="userIntegration.id"
          :user-integration-flow-id="userIntegrationFlow.id"
          type="data_mapping"
          @close="showLoadTemplateModal = false"
        />

        <flow-store-template-modal
          v-if="isAdmin"
          :show="showStoreTemplateModal"
          :user-integration-id="userIntegration.id"
          :user-integration-flow-id="userIntegrationFlow.id"
          type="data_mapping"
          @close="showStoreTemplateModal = false"
        />
      </loading-view>
    </div>
  </div>
</template>
<script>
import DashboardLayout from '@/Layouts/DashboardLayout';
import IntegrationFlowLayout from '@/Layouts/IntegrationFlowLayout';

export default {
  layout: [DashboardLayout, IntegrationFlowLayout],
};
</script>
